import React from "react";
import {
  RefreshIcon,
  ScaleIcon,
  SparklesIcon,
  CheckCircleIcon,
  ClockIcon,
} from "@heroicons/react/outline";
import Badge from "../../../../components/common/Badge";

const ExchangeTab = ({ order, formatCurrency, totalExchangeValue }) => {
  if (!order.exchangeItems || order.exchangeItems.length === 0) {
    return (
      <div className="text-center py-10 sm:py-16 px-4">
        <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <RefreshIcon className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400" />
        </div>
        <p className="text-sm sm:text-base text-gray-500 font-medium">
          No exchange items in this order
        </p>
        <p className="text-xs sm:text-sm text-gray-400 mt-1">
          Exchange items will appear here once added
        </p>
      </div>
    );
  }

  // Calculate summary
  const exchangeSummary = {
    totalItems: order.exchangeItems.length,
    acceptedItems: order.exchangeItems.filter((i) => i.status === "ACCEPTED")
      .length,
    totalWeight: order.exchangeItems.reduce(
      (sum, item) => sum + (item.netWeight || 0),
      0
    ),
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Exchange Summary Header */}
      <div className="flex items-center gap-2 sm:gap-4 px-1 py-2 text-xs sm:text-sm text-gray-500 border-b border-gray-100">
        <div className="flex items-center gap-1.5">
          <RefreshIcon className="w-4 h-4" />
          <span>
            <span className="font-medium text-gray-700">
              {exchangeSummary.totalItems}
            </span>{" "}
            {exchangeSummary.totalItems === 1 ? "Item" : "Items"}
          </span>
        </div>
        <div className="w-px h-4 bg-gray-200" />
        <div className="flex items-center gap-1.5">
          <CheckCircleIcon className="w-4 h-4 text-green-500" />
          <span>
            <span className="font-medium text-green-600">
              {exchangeSummary.acceptedItems}
            </span>{" "}
            Accepted
          </span>
        </div>
        <div className="w-px h-4 bg-gray-200" />
        <div className="flex items-center gap-1.5">
          <ScaleIcon className="w-4 h-4" />
          <span>
            <span className="font-medium text-gray-700">
              {exchangeSummary.totalWeight.toFixed(2)}
            </span>
            g
          </span>
        </div>
      </div>

      {/* Exchange Items List */}
      {order.exchangeItems.map((item, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden bg-white
                     shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          {/* Item Header */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-amber-50 to-white border-b border-gray-100">
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-amber-100 text-amber-700 text-xs sm:text-sm font-bold">
                {index + 1}
              </span>
              <div className="min-w-0">
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                  {item.itemType || "Exchange"} Item
                </h4>
                <p className="text-xs sm:text-sm text-gray-500 truncate max-w-[180px] sm:max-w-none">
                  {item.description || `Exchange #${index + 1}`}
                </p>
              </div>
            </div>
            <Badge
              variant={item.status === "ACCEPTED" ? "green" : "yellow"}
              size="sm"
              className="text-[10px] sm:text-xs flex-shrink-0"
            >
              {item.status === "ACCEPTED" ? (
                <CheckCircleIcon className="w-3 h-3 mr-1" />
              ) : (
                <ClockIcon className="w-3 h-3 mr-1" />
              )}
              {item.status || "PENDING"}
            </Badge>
          </div>

          {/* Item Details */}
          <div className="p-3 sm:p-4">
            {/* Weight & Details Pills */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
              <span className="inline-flex items-center px-2 sm:px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700 text-[11px] sm:text-xs">
                <ScaleIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 text-gray-400" />
                <span className="text-gray-400 mr-1">Gross:</span>
                <span className="font-semibold">{item.grossWeight || 0}g</span>
              </span>
              <span className="inline-flex items-center px-2 sm:px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700 text-[11px] sm:text-xs">
                <span className="text-gray-400 mr-1">Net:</span>
                <span className="font-semibold">{item.netWeight || 0}g</span>
              </span>
              {item.stoneWeight > 0 && (
                <span className="inline-flex items-center px-2 sm:px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 text-[11px] sm:text-xs">
                  <SparklesIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 text-purple-400" />
                  <span className="text-purple-400 mr-1">Stone:</span>
                  <span className="font-semibold">{item.stoneWeight}g</span>
                </span>
              )}
              <span className="inline-flex items-center px-2 sm:px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-[11px] sm:text-xs">
                <span className="text-amber-500 mr-1">Purity:</span>
                <span className="font-semibold">
                  {item.metalDetails?.purity || "-"}
                </span>
              </span>
              <span className="inline-flex items-center px-2 sm:px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-[11px] sm:text-xs">
                <span className="text-blue-400 mr-1">Condition:</span>
                <span className="font-semibold">
                  {item.conditionGrade || "Good"}
                </span>
              </span>
            </div>

            {/* System Appraisal */}
            {item.systemAppraisal && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3">
                <p className="text-xs sm:text-sm font-semibold text-blue-900 mb-2 sm:mb-3 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  System Appraisal
                </p>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {/* Gross Value */}
                  <div className="bg-white/60 rounded-lg p-2 sm:p-2.5">
                    <p className="text-[10px] sm:text-xs text-blue-600 mb-0.5">
                      Gross Value
                    </p>
                    <p className="text-sm sm:text-base font-semibold text-blue-900">
                      {formatCurrency(item.systemAppraisal.grossValue || 0)}
                    </p>
                  </div>
                  {/* Deductions */}
                  <div className="bg-white/60 rounded-lg p-2 sm:p-2.5">
                    <p className="text-[10px] sm:text-xs text-red-500 mb-0.5">
                      Deductions
                    </p>
                    <p className="text-sm sm:text-base font-semibold text-red-600">
                      -
                      {formatCurrency(
                        item.systemAppraisal.totalDeductions || 0
                      )}
                    </p>
                  </div>
                  {/* System Final Value - Full Width */}
                  <div className="col-span-2 bg-blue-100/50 rounded-lg p-2 sm:p-2.5 flex items-center justify-between">
                    <p className="text-xs sm:text-sm text-blue-700 font-medium">
                      System Final Value
                    </p>
                    <p className="text-base sm:text-lg font-bold text-blue-900">
                      {formatCurrency(item.systemAppraisal.finalValue || 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Final Agreed Value */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-green-800">
                    Final Agreed Value
                  </p>
                  {item.agreedAt && (
                    <p className="text-[10px] sm:text-xs text-green-600 mt-0.5 flex items-center gap-1">
                      <CheckCircleIcon className="w-3 h-3" />
                      Agreed on {new Date(item.agreedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <span className="text-xl sm:text-2xl font-bold text-green-600">
                  {formatCurrency(item.agreedValue || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Total Exchange Value Card */}
      <div
        className="relative overflow-hidden bg-gradient-to-br from-green-500 to-emerald-600 
                      rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-lg"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white" />
          <div className="absolute -left-4 -bottom-4 w-32 h-32 rounded-full bg-white" />
        </div>

        <div className="relative flex items-center justify-between">
          <div>
            <p className="text-green-100 text-xs sm:text-sm font-medium mb-0.5">
              Total Exchange Value
            </p>
            <p className="text-white/70 text-[10px] sm:text-xs">
              {order.exchangeItems.length} item
              {order.exchangeItems.length !== 1 ? "s" : ""} exchanged
            </p>
          </div>
          <div className="text-right">
            <span className="text-2xl sm:text-3xl font-bold text-white">
              {formatCurrency(totalExchangeValue)}
            </span>
          </div>
        </div>

        {/* Decorative Icon */}
        <div className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 opacity-20">
          <RefreshIcon className="w-16 h-16 sm:w-20 sm:h-20 text-white" />
        </div>
      </div>
    </div>
  );
};

export default ExchangeTab;
